<template>
  <modal 
    name="report-damage" 
    class="modal-inner" 
    transition="pop-out" 
    :width="modalWidth" 
    :focus-trap="true" 
    :min-height="400" 
    height="auto" 
    :scrollable="true" 
    :adaptive="true" 
    :reset="true"
    @before-open="beforeOpen"
  >
    <span class="close-button" @click="hide('report-damage')">
      <BaseIcon icon="times-circle" class="text-white"/>
    </span>
    <div class="modal-header text-lg bg-primary-50 font-serif">
      {{ $t("report_damage.title") }}
    </div>
    <div class="modal-body">
      <div v-if="isLoading" class="border border-gray-200 rounded-lg mt-3 py-3 px-3 font-sm text-gray-500 text-center">
        <BaseIcon icon="spinner" spin />
      </div>
      
      <div v-else class="space-y-4">
        <!-- Device Selection -->
        <div class="form-group">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("report_damage.which_device") }}
          </label>
          <select 
            v-model="selectedDevice" 
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :class="{ 'border-red-500': errors.device }"
          >
            <option :value="null" disabled>{{ $t("report_damage.select_device") }}</option>
            <option 
              v-for="alarm in devices" 
              :key="alarm._id" 
              :value="alarm"
            >
              {{ alarm.unit_name }} - {{ alarm.modelnumber }} ({{ alarm.imei_number }})
            </option>
          </select>
          <span v-if="errors.device" class="text-red-500 text-xs mt-1">{{ errors.device }}</span>
        </div>

        <!-- Description Input -->
        <div class="form-group">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            {{ $t("report_damage.what_happened") }}
          </label>
          <textarea 
            v-model="description" 
            rows="6"
            class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            :class="{ 'border-red-500': errors.description }"
            :placeholder="$t('report_damage.description_placeholder')"
          ></textarea>
          <span v-if="errors.description" class="text-red-500 text-xs mt-1">{{ errors.description }}</span>
        </div>

        <!-- Action Buttons -->
        <div class="flex justify-end gap-3 mt-6">
          <button 
            class="btn-secondary-outline" 
            @click="hide('report-damage')"
          >
            {{ $t("user_actions.cancel") }}
          </button>
          <button 
            class="btn-green" 
            @click="submitDamageReport"
            :disabled="isSubmitting"
          >
            <BaseIcon v-if="isSubmitting" icon="spinner" spin class="mr-2" />
            {{ $t("report_damage.submit") }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
const MODAL_WIDTH = 600;

export default {
  props: {
    agreement: {
      type: Object,
      required: false,
      default: null
    }
  },

  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      devices: [],
      selectedDevice: null,
      description: '',
      errors: {
        device: '',
        description: ''
      },
      currentAgreement: null
    };
  },

  computed: {
    modalWidth() {
      return MODAL_WIDTH;
    }
  },

  methods: {
    async beforeOpen(event) {
      // Get agreement from event params or prop
      this.currentAgreement = event.params?.agreement || this.agreement;
      
      if (!this.currentAgreement) {
        console.error('No agreement provided');
        return;
      }
      
      // Reset form
      this.selectedDevice = null;
      this.description = '';
      this.errors = { device: '', description: '' };
      
      // Load devices
      await this.loadDevices();
    },

    async loadDevices() {
        if(!this.currentAgreement){ return false }

        this.isLoading = true;

        try{
            const response = await this.axios.get(
                `${process.env.VUE_APP_SERVER_URL}/agreement/alarms/${this.currentAgreement.agreement_number}`
            );
            this.devices = []

            if(response.data.customers){
                response.data.customers.forEach(customer => {
                    customer.units.forEach(unit => {
                        unit.alarms.forEach(alarm => {
                            this.devices.push(alarm);
                        });
                    });
                });
            }
        }catch(error){
            this.handleError(error);
        } finally {
            this.isLoading = false;
        }
    },

    validate() {
      this.errors = { device: '', description: '' };
      let isValid = true;

      if (!this.selectedDevice) {
        this.errors.device = this.$t('report_damage.error_device_required');
        isValid = false;
      }

      if (!this.description || this.description.trim().length === 0) {
        this.errors.description = this.$t('report_damage.error_description_required');
        isValid = false;
      }

      if (this.description && this.description.trim().length < 10) {
        this.errors.description = this.$t('report_damage.error_description_too_short');
        isValid = false;
      }

      return isValid;
    },

    async submitDamageReport() {
      if (!this.validate()) {
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          agreement_number: this.currentAgreement.agreement_number,
          device_imei: this.selectedDevice.imei_number,
          device_name: this.selectedDevice.unit_name,
          device_model: this.selectedDevice.modelnumber,
          description: this.description.trim()
        };
        await this.axios.put(
          `${process.env.VUE_APP_SERVER_URL}/agreement/damage-report/${this.currentAgreement._id}`,
          payload
        );
        this.handleSuccess(this.$t('report_damage.success_message'));
        this.hide('report-damage');
        this.$emit('damage-reported');
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isSubmitting = false;
      }
    },
    hide(id) {
      this.$modal.hide(id);
    }
  }
};
</script>
